import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Search, CheckCircle2, Megaphone, History } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SeatMap from "@/components/election/SeatMap";
import { useAuth } from "@/context/AuthContext";
import { isGeneralManager } from "@/lib/roles";

const fold = (s) => (s || "").toLocaleLowerCase("tr");
const autoWinner = (o, r) => {
  const a = Number(o), b = Number(r);
  if (o === "" || r === "" || Number.isNaN(a) || Number.isNaN(b)) return null;
  if (a > b) return "biz";
  if (b > a) return "karsi";
  return "kura";
};

function ResultRow({ r, onSaved, isGM }) {
  const [f, setF] = useState({
    our: r.our_votes ?? "", rival: r.rival_votes ?? "", invalid: r.invalid ?? "",
    winner: r.winner_manual ? r.winner : "auto", note: r.note || "",
    lot: r.lottery_our_seats ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const dirtyKey = `${r.our_votes}|${r.rival_votes}|${r.invalid}|${r.winner_manual ? r.winner : "auto"}|${r.note}|${r.lottery_our_seats}`;
  useEffect(() => {
    setF({
      our: r.our_votes ?? "", rival: r.rival_votes ?? "", invalid: r.invalid ?? "",
      winner: r.winner_manual ? r.winner : "auto", note: r.note || "",
      lot: r.lottery_our_seats ?? "",
    });
  }, [dirtyKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const effective = f.winner === "auto" ? autoWinner(f.our, f.rival) : f.winner;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/election-day/results/${r.committee_no}`, {
        our_votes: f.our === "" ? null : f.our,
        rival_votes: f.rival === "" ? null : f.rival,
        invalid: f.invalid === "" ? null : f.invalid,
        winner: f.winner === "auto" ? null : f.winner,
        lottery_our_seats: f.lot === "" ? null : f.lot,
        note: f.note,
      });
      toast.success(`K${r.committee_no} sonucu kaydedildi`);
      onSaved(data.seats);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/election-day/results/${r.committee_no}`);
      if (data.deleted) toast.success(`K${r.committee_no} sonucu silindi`);
      onSaved(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(false);
    }
  };

  const won = effective === "biz";

  return (
    <tr className="border-t border-border align-top" data-testid={`result-row-${r.committee_no}`}>
      <td className="px-2 py-2 font-semibold">{r.committee_no}</td>
      <td className="px-2 py-2 max-w-[180px]">
        <span className="block truncate">{r.name}</span>
        <span className="text-[11px] text-muted-foreground">{r.seats} meclis · %{r.participation} katılım</span>
      </td>
      <td className="px-2 py-2 text-xs tabular-nums whitespace-nowrap">
        <span className="text-emerald-700 dark:text-emerald-400 font-semibold">{r.biz}</span>
        {" / "}
        <span className="text-red-600">{r.karsi}</span>
        <span className={`block font-semibold ${r.lead > 0 ? "text-emerald-600" : r.lead < 0 ? "text-red-600" : "text-muted-foreground"}`}>
          {r.lead > 0 ? `+${r.lead}` : r.lead}
        </span>
      </td>
      <td className="px-1 py-2"><Input className="h-8 w-[74px] text-sm" type="number" min="0" value={f.our}
        disabled={!r.can_edit} onChange={(e) => setF({ ...f, our: e.target.value })}
        data-testid={`result-our-${r.committee_no}`} /></td>
      <td className="px-1 py-2"><Input className="h-8 w-[74px] text-sm" type="number" min="0" value={f.rival}
        disabled={!r.can_edit} onChange={(e) => setF({ ...f, rival: e.target.value })}
        data-testid={`result-rival-${r.committee_no}`} /></td>
      <td className="px-1 py-2"><Input className="h-8 w-[66px] text-sm" type="number" min="0" value={f.invalid}
        disabled={!r.can_edit} onChange={(e) => setF({ ...f, invalid: e.target.value })}
        data-testid={`result-invalid-${r.committee_no}`} /></td>
      <td className="px-1 py-2">
        <Select value={f.winner} onValueChange={(v) => setF({ ...f, winner: v })} disabled={!r.can_edit}>
          <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`result-winner-${r.committee_no}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Otomatik</SelectItem>
            <SelectItem value="biz">Bizim liste</SelectItem>
            <SelectItem value="karsi">Rakip liste</SelectItem>
            <SelectItem value="kura">Beraberlik · meclis kurası</SelectItem>
          </SelectContent>
        </Select>
        <span className="mt-1 block text-[11px]">
          {effective === "biz" && <Badge className="bg-emerald-600 text-white text-[10px]">Bizim liste · +{r.seats}</Badge>}
          {effective === "karsi" && <Badge className="bg-red-600 text-white text-[10px]">Rakip liste</Badge>}
          {effective === "kura" && (
            <Badge className="bg-amber-600 text-white text-[10px]">
              Beraberlik · kura ({r.seats} üye)
            </Badge>
          )}
          {!effective && <span className="text-muted-foreground">belirsiz</span>}
        </span>
        {effective === "kura" && (
          <div className="mt-1.5 rounded border border-amber-400 bg-amber-50 p-1.5 dark:bg-amber-950/40"
            data-testid={`result-lottery-box-${r.committee_no}`}>
            <p className="text-[10px] font-semibold leading-tight">
              Kurada bize düşen üye ({r.seats} üyelik · torbada {r.seats * 2} aday)
            </p>
            <Input className="mt-1 h-8 w-[74px] text-sm" type="number" min="0" max={r.seats}
              value={f.lot} disabled={!r.can_edit}
              onChange={(e) => setF({ ...f, lot: e.target.value })}
              data-testid={`result-lottery-${r.committee_no}`} />
            <p className="mt-0.5 text-[10px] text-muted-foreground">
              Rakibe düşen: {f.lot === "" ? "—" : Math.max(0, r.seats - Number(f.lot))}
            </p>
          </div>
        )}
      </td>
      <td className="px-1 py-2"><Input className="h-8 w-[130px] text-sm" value={f.note} placeholder="Not"
        disabled={!r.can_edit} onChange={(e) => setF({ ...f, note: e.target.value })}
        data-testid={`result-note-${r.committee_no}`} /></td>
      <td className="px-1 py-2 whitespace-nowrap">
        {r.can_edit ? (
          <span className="flex gap-1">
            <Button size="sm" className={`h-8 px-2 ${won ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
              onClick={save} disabled={busy} data-testid={`result-save-${r.committee_no}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            </Button>
            {r.updated_at && (
              <Button size="sm" variant="ghost" className="h-8 px-2" onClick={clear} disabled={busy}
                data-testid={`result-clear-${r.committee_no}`}>
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </Button>
            )}
            {isGM && r.updated_at && r.winner && (
              <Button size="sm" variant="ghost" className="h-8 px-2" disabled={busy}
                data-testid={`result-announce-${r.committee_no}`}
                onClick={() => api.post(`/election-day/results/announce/${r.committee_no}`)
                  .then(() => toast.success("Sonuç ekibe yeniden duyuruldu"))
                  .catch((e) => toast.error(formatApiError(e.response?.data?.detail, e)))}>
                <Megaphone className="h-3.5 w-3.5 text-sky-600" />
              </Button>
            )}
          </span>
        ) : <span className="text-[11px] text-muted-foreground">yetki yok</span>}
        {r.updated_by && (
          <span className="mt-1 block text-[10px] text-muted-foreground">
            {r.updated_by} · {String(r.updated_at || "").slice(11, 16)}
          </span>
        )}
        {(r.history || []).length > 0 && (
          <>
            <button type="button" onClick={() => setShowHist((v) => !v)}
              data-testid={`result-history-toggle-${r.committee_no}`}
              className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-sky-700 dark:text-sky-400">
              <History className="h-3 w-3" /> Tarihçe ({r.history.length})
            </button>
            {showHist && (
              <div className="mt-1 max-h-32 w-[260px] overflow-y-auto rounded border border-border bg-muted/50 p-1.5 text-[10px] leading-snug"
                data-testid={`result-history-${r.committee_no}`}>
                {r.history.map((h, i) => (
                  <div key={i} className="border-b border-border/50 py-0.5 last:border-0">
                    <b>{String(h.at || "").slice(0, 16).replace("T", " ")} · {h.by}</b>
                    <div>
                      {h.old?.our_votes ?? "—"}/{h.old?.rival_votes ?? "—"} → {h.new?.our_votes ?? "—"}/{h.new?.rival_votes ?? "—"}
                      {h.old?.winner !== h.new?.winner && ` · kazanan: ${h.old?.winner || "—"} → ${h.new?.winner || "—"}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

export default function CommitteeResults() {
  const { user } = useAuth();
  const isGM = isGeneralManager(user);
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/election-day/results");
      setD(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="flex items-center gap-2 text-muted-foreground p-6"><Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor...</div>;

  let rows = d.rows;
  if (onlyMine) rows = rows.filter((r) => r.can_edit);
  if (q.trim()) rows = rows.filter((r) => fold(r.name).includes(fold(q)) || String(r.committee_no) === q.trim());

  return (
    <div className="space-y-5" data-testid="committee-results">
      <SeatMap seats={d.seats} />

      <Card className="p-0 border border-border overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 p-4 pb-2">
          <div>
            <h3 className="font-display font-semibold text-base md:text-lg">Komite sonuçları</h3>
            <p className="text-xs text-muted-foreground">
              Sayım açıklandıkça girin. Kazanan otomatik hesaplanır (çok oy alan liste), istersen
              elle değiştirin. Kazanan liste o komitenin <b>tüm</b> meclis koltuklarını alır.
              Seçim gününe kadar girilen sonuçlar için kimseye bildirim gitmez.
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant={onlyMine ? "default" : "outline"} onClick={() => setOnlyMine((v) => !v)}
              data-testid="results-only-mine">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Yetkili olduklarım
            </Button>
            <div className="relative w-44">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Komite ara"
                className="pl-8 h-9" data-testid="results-search" />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[940px]">
            <thead className="bg-muted/80 text-xs">
              <tr className="text-left">
                {["K", "Komite", "Canlı (biz/rakip)", "Bizim oy", "Rakip oy", "Geçersiz", "Kazanan", "Not", ""].map((h) => (
                  <th key={h} className="px-2 py-2 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <ResultRow key={r.committee_no} r={r} isGM={isGM}
                  onSaved={(seats) => { if (seats) setD((s) => ({ ...s, seats })); load(); }} />
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && <p className="p-4 text-sm text-muted-foreground">Kayıt bulunamadı.</p>}
      </Card>
    </div>
  );
}
